import { useOpenModal } from '@flowus/common/next-modal';
import { CollectionViewType } from '@next-space/fe-api-idl';
import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';
import { Icon } from 'src/common/components/icon';
import { Tooltip } from 'src/common/components/tooltip';
import { SHARE_LINK, UNTITLED } from 'src/common/const';
import { segmentsToText } from 'src/editor/utils/editor';
import { useCollectionView } from 'src/hooks/collection-view/use-collection-view';
import { usePayInfo } from 'src/hooks/page/use-pay-info';
import { useCurrentUser } from 'src/hooks/user/use-current-user';
import { useIsLogin } from 'src/hooks/user/use-is-login';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { usePickBlock } from 'src/utils/pick-block';
import { useOpenLogin } from '../empty/open-login';
import { useOpenWeappQR } from '../weapp-qr';
import { useGetSharePageUrl } from './helper';
import { useOpenAICover, useOpenShareLinkQr } from './share-link-qr';
import { useOpenSharePoster } from './share-poster';
import { ShareDialog } from './share-dialog';
import { Button } from '@flowus/common/components';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { cx } from '@flowus/common/cx';

interface ShareButtonsProps {
  uuid: string;
  url: string;
  onCopyLink?: () => void;
  onCopyEmbedLink?: () => void;
}
export const ShareButtons: FC<ShareButtonsProps> = (props) => {
  const { url, onCopyLink, uuid, onCopyEmbedLink } = props;
  const block = usePickBlock(uuid, ['data'], ['segments']);
  const collectionView = useCollectionView(uuid);
  const openWeappQR = useOpenWeappQR();
  const openShareLinkQr = useOpenShareLinkQr();
  const openSharePoster = useOpenSharePoster();
  const openAICover = useOpenAICover();
  const { enableAI } = useEnableAI();

  let title = `分享了${segmentsToText(block?.data.segments) ?? UNTITLED}`;
  if (collectionView?.type === CollectionViewType.FORM) {
    title = '邀请你填写收集表';
  }

  return (
    <div className="px-4 border-t border-grey6 text-t2 bg-grey9 flex items-center  h-12">
      {__BUILD_IN__ ? (
        <span className="mr-4 text-black">分享方式</span>
      ) : (
        <Button
          colorType="grey"
          className={cx('border border-grey6 bg-transparent rounded-[20px]', {
            hidden: !enableAI,
          })}
          onClick={() => {
            openAICover();
          }}
        >
          <Icon name="IcShare" size="middle" className="text-normal/65" />
          <div className="text-grey2 text-t4-medium ml-0.5">分享 AI 海报</div>
        </Button>
      )}
      <Tooltip popup={SHARE_LINK} className="animate-click mr-2 ml-2 rounded-full border p-1">
        <Icon name="IcLink" size="middle" className="text-normal/65" onClick={onCopyLink} />
      </Tooltip>
      {onCopyEmbedLink && (
        <Tooltip popup={'内嵌模式分享'} className="animate-click mr-2 rounded-full border p-1">
          <Icon
            name="IcGlobeShare"
            size="middle"
            className="text-normal/65"
            onClick={onCopyEmbedLink}
          />
        </Tooltip>
      )}
      {!(__BUILD_IN__ || __PRIVATE__) && (
        <Tooltip
          popup="小程序分享"
          onClick={() => openWeappQR({ url })}
          className="animate-click mr-2 rounded-full border p-1"
        >
          <Icon name="IcMenuMiniProgramQrcode" size="middle" className="text-normal/65" />
        </Tooltip>
      )}
      {!__BUILD_IN__ && (
        <Tooltip
          popup="生成海报，可保存图片"
          className="animate-click mr-2 rounded-full border p-1"
          onClick={() => openSharePoster({ url, pageId: uuid })}
        >
          <Icon name="IcImage" size="middle" className="text-normal/65" />
        </Tooltip>
      )}
      <Tooltip
        popup="二维码分享"
        className="animate-click rounded-full border p-1"
        onClick={(e) => openShareLinkQr(e, { url, title })}
      >
        <Icon name="IcQrCode" size="middle" className="text-normal/65" />
      </Tooltip>
    </div>
  );
};

export const useOpenShareLinkList = (uuid: string) => {
  const openLogin = useOpenLogin();
  const isLogin = useIsLogin();
  const openModal = useOpenModal();

  return useCallback(
    async (event: MouseEvent) => {
      if (!isLogin) {
        openLogin();
        return;
      }

      openModal.dropdown({
        popcorn: event?.currentTarget,
        placement: 'bottom',
        content: () => {
          return <ShareDialog pageId={uuid} />;
        },
      });
    },
    [isLogin, openLogin, openModal, uuid]
  );
};
